/**
 * Central Billing REST Client
 *
 * Calls Central billing endpoints DIRECTLY from the browser.
 * Same pattern as centralBootstrap.ts and centralNotifications.ts.
 *
 * Uses shared types and path constants from @levelup/central-billing.
 * App-local React Query wiring is in hooks/useCreditBalance.ts.
 */

import {
  SUBSCRIPTION_STATUS_PATH,
  BILLING_PROVIDER_PATH,
  BILLING_CHECKOUT_PATH,
  BILLING_PORTAL_PATH,
  PLANS_PATH,
} from '@levelup/central-billing';

/**
 * Credit wallet balance endpoint.
 *
 * @levelup/central-billing@1.0.0 declares WALLET_BALANCE_PATH = "/api/wallet/balance",
 * but central-staging hasn't shipped that route yet — it returns 404 in the
 * deployed Central. CRM resolved the same issue by calling "/api/credits/balance",
 * which their server-side centralContext.ts uses successfully with Bearer auth
 * against this exact Central deployment. We use the verified path here until
 * the shared constant and Central deployment realign.
 */
const CREDITS_BALANCE_PATH = '/api/credits/balance';
import type {
  CreditWallet,
  CreditTransaction,
  SubscriptionInfo,
  BillingProvider,
  CheckoutRequest,
  CheckoutResponse,
  PortalSessionResponse,
  PlanCatalogItem,
} from '@levelup/central-billing';
import { getAccessToken } from './centralAuth';

const RAW_CENTRAL_URL = import.meta.env.VITE_CENTRAL_URL;

function getCentralBase(): string | null {
  if (!RAW_CENTRAL_URL) return null;
  return RAW_CENTRAL_URL.replace(/\/+$/, '');
}

export class CentralBillingError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'CentralBillingError';
  }
}

async function centralBillingFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getAccessToken();
  if (!token) {
    throw new CentralBillingError(401, 'Not authenticated');
  }

  const centralBase = getCentralBase();
  if (!centralBase) {
    throw new CentralBillingError(0, 'VITE_CENTRAL_URL not configured');
  }

  const url = new URL(path, centralBase).toString();

  const response = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (response.status === 401 || response.status === 403) {
    throw new CentralBillingError(response.status, 'Unauthorized');
  }

  if (!response.ok) {
    throw new CentralBillingError(response.status, `Central returned ${response.status}`);
  }

  return response.json();
}

// ---------------------------------------------------------------------------
// Wallet / Credit Balance
// ---------------------------------------------------------------------------

export interface WalletBalanceResponse {
  ok: true;
  wallet: CreditWallet;
  recentTransactions?: CreditTransaction[];
}

/**
 * Fetch the workspace's credit wallet balance from Central.
 *
 * Returns an empty wallet with balance=0 when Central responds 404
 * (workspace has no wallet yet — valid initial state, not an error).
 */
export async function fetchCreditBalance(
  workspaceId: string,
): Promise<WalletBalanceResponse> {
  const params = new URLSearchParams({ workspaceId });
  try {
    return await centralBillingFetch<WalletBalanceResponse>(
      `${CREDITS_BALANCE_PATH}?${params.toString()}`,
    );
  } catch (err) {
    if (err instanceof CentralBillingError && err.status === 404) {
      return {
        ok: true,
        wallet: { id: workspaceId, name: 'workspace', balance: 0 } as CreditWallet,
        recentTransactions: [],
      };
    }
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Subscription Status
// ---------------------------------------------------------------------------

export interface SubscriptionStatusResponse {
  ok: true;
  subscription: SubscriptionInfo;
}

/**
 * Fetch the workspace's subscription status from Central.
 * 404 → free plan (no paid subscription yet).
 */
export async function fetchSubscriptionStatus(
  workspaceId: string,
): Promise<SubscriptionStatusResponse> {
  const params = new URLSearchParams({ workspaceId });
  try {
    return await centralBillingFetch<SubscriptionStatusResponse>(
      `${SUBSCRIPTION_STATUS_PATH}?${params.toString()}`,
    );
  } catch (err) {
    if (err instanceof CentralBillingError && err.status === 404) {
      return {
        ok: true,
        subscription: {
          planCode: 'free',
          status: 'active',
          currentPeriodEnd: null,
          cancelAtPeriodEnd: false,
          trialEndsAt: null,
        } as SubscriptionInfo,
      };
    }
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Billing Provider Discovery
// ---------------------------------------------------------------------------

export interface BillingProviderResponse {
  ok: true;
  provider: BillingProvider;
  checkoutEnabled: boolean;
  portalEnabled: boolean;
}

/**
 * Discover the billing provider for the current workspace.
 * Returns provider type and which capabilities are enabled.
 */
export async function fetchBillingProvider(
  workspaceId: string,
): Promise<BillingProviderResponse> {
  const params = new URLSearchParams({ workspaceId });
  return centralBillingFetch<BillingProviderResponse>(
    `${BILLING_PROVIDER_PATH}?${params.toString()}`,
  );
}

// ---------------------------------------------------------------------------
// Stripe Checkout
// ---------------------------------------------------------------------------

/**
 * Create a Stripe checkout session for a plan upgrade.
 * Returns a URL to redirect the user to Stripe.
 */
export async function createCheckoutSession(
  workspaceId: string,
  request: CheckoutRequest,
): Promise<CheckoutResponse> {
  return centralBillingFetch<CheckoutResponse>(
    BILLING_CHECKOUT_PATH,
    {
      method: 'POST',
      body: JSON.stringify({ workspaceId, ...request }),
    },
  );
}

// ---------------------------------------------------------------------------
// Billing Portal
// ---------------------------------------------------------------------------

/**
 * Create a Stripe billing portal session.
 * Returns a URL to redirect the user to Stripe's self-service portal.
 */
export async function createPortalSession(
  workspaceId: string,
): Promise<PortalSessionResponse> {
  return centralBillingFetch<PortalSessionResponse>(
    BILLING_PORTAL_PATH,
    {
      method: 'POST',
      body: JSON.stringify({ workspaceId }),
    },
  );
}

// ---------------------------------------------------------------------------
// Plan Catalog
// ---------------------------------------------------------------------------

export interface PlanCatalogResponse {
  ok: true;
  plans: PlanCatalogItem[];
}

/**
 * Fetch the available plan catalog from Central.
 */
export async function fetchPlanCatalog(
  workspaceId: string,
): Promise<PlanCatalogResponse> {
  const params = new URLSearchParams({ workspaceId });
  return centralBillingFetch<PlanCatalogResponse>(
    `${PLANS_PATH}?${params.toString()}`,
  );
}
